import React, { Fragment, useEffect, useRef } from "react";
import {
  useLocation,
  useOutletContext,
  Link,
  useBlocker,
} from "react-router-dom";
import "material-symbols/outlined.css";
import { Button } from "components/button";
import {
  checkIfFullKiosk,
  getQueryParams,
  useCurrentLocation,
} from "lib/utils";
import { useRestartIfAbsent } from "hooks/useRestartIfAbsent";
import { useTranslation } from "react-i18next";
import { TAKEBACK_METHOD_NAME } from "consts/query-params";
import storeLocations from "consts/stores.json";
import weightRestrictions from "consts/weight-restrictions";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { calculateDurationByWeight } from "lib/utils";
import ConfirmNavigationModal from "components/confirm-navigation-modal";
import { AnimatePresence } from "framer-motion";
import FloodlightEvent from "components/floodlight-event";
// import { Helmet } from "react-helmet";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function NextStepsPage() {
  const { t } = useTranslation();

  // Update right side visual
  const { setSidebarVisual } = useOutletContext();
  useEffect(() => {
    setSidebarVisual("face");
  }, [setSidebarVisual]);

  // Get query params and restart flow if absent
  const location = useLocation();
  const queryParams = getQueryParams(location.search);
  useRestartIfAbsent(
    queryParams,
    ["w", TAKEBACK_METHOD_NAME, "p"],
    t("urls.trade_in_weight")
  );

  const matchingAddress = storeLocations.find(
    (location) => location.value === queryParams[TAKEBACK_METHOD_NAME]
  )?.address;
  const addressString = matchingAddress
    ? matchingAddress
    : t("trade-in.next_steps.address_not_found");

  const { location: currentLocation } = useCurrentLocation();
  const isKiosk = checkIfFullKiosk(currentLocation);

  // Get appointment info for in-store dropoff
  // (see summary.jsx for logic)
  const appointmentSlot = JSON.parse(
    localStorage.getItem("appointment") || "{}"
  );
  const appointment = appointmentSlot.appointment || {};

  const extractStateFromReturnType = (returnType) => {
    if (!returnType) return null;
    const match = returnType.match(/in_store_(\w{2})/i);
    return match ? match[1].toUpperCase() : null;
  };

  const getTimezoneForState = (state) => {
    const timezoneMap = {
      CO: "America/Denver", // MDT
      AR: "America/Chicago", // CDT
      NJ: "America/New_York", // EDT
      PA: "America/New_York", // EDT
    };
    const extractedState = extractStateFromReturnType(state);
    return timezoneMap[extractedState] || dayjs.tz.guess();
  };

  const getTimezoneAbbreviationForState = (returnType) => {
    const state = extractStateFromReturnType(returnType);
    switch (state) {
      case "CO":
        return "MDT";
      case "AR":
        return "CDT";
      case "NJ":
      case "PA":
        return "EDT";
      default:
        return "";
    }
  };

  const formatAppointmentTimeRange = (startISO, duration, returnType) => {
    if (!startISO) return null;
    const timezone = getTimezoneForState(returnType);
    const tzAbbr = getTimezoneAbbreviationForState(returnType);
    const startObj = dayjs(startISO).tz(timezone);
    const durationMinutes = duration || 30;
    const endObj = startObj.add(durationMinutes, "minute");
    return (
      startObj.format("dddd, MMMM D") +
      " at " +
      startObj.format("h:mm A") +
      " - " +
      endObj.format("h:mm A") +
      (tzAbbr ? ` ${tzAbbr}` : "")
    );
  };

  // Determine which version to show
  const isMail = queryParams[TAKEBACK_METHOD_NAME] === "mail";

  const currentPath = t("urls.trade_in_next_steps");
  const prevPath = t("urls.trade_in_summary");

  //block back button and display modal
  let blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      prevPath === nextLocation.pathname &&
      currentPath === currentLocation.pathname
  );

  //meta pixel events
  const hasSentFbqGeneralEvent = useRef(false);
  const hasSentFbqMethodEvent = useRef(false);

  useEffect(() => {
    if (!hasSentFbqGeneralEvent.current && typeof window.fbq === "function") {
      if (window.getCkyConsent && window.getCkyConsent().categories.analytics) {
        window.fbq("trackCustom", "TradeInCreated");
        hasSentFbqGeneralEvent.current = true;
      }
    }
  }, []);

  useEffect(() => {
    const method = queryParams[TAKEBACK_METHOD_NAME];

    if (
      method &&
      !hasSentFbqMethodEvent.current &&
      typeof window.fbq === "function"
    ) {
      if (window.getCkyConsent && window.getCkyConsent().categories.analytics) {
        const eventName =
          method === "mail" ? "TradeInCreatedMail" : "TradeInCreatedInStore";
        window.fbq("trackCustom", eventName);
        hasSentFbqMethodEvent.current = true;
      }
    }
  }, [queryParams]);

  return (
    <div className="h-full flex flex-col justify-between">
      <FloodlightEvent
        tag={
          isMail
            ? "DC-14832541/pageview/pl_nsmb+per_session"
            : "DC-14832541/pageview/pl_nsis+per_session"
        }
      />

      <AnimatePresence>
        {blocker.state === "blocked" && (
          <ConfirmNavigationModal
            title={t("ui.confirm_navigation_modal.title")}
            body={
              <span>
                Visit the My Trade-Ins Dashboard to view or edit your trade-in.
              </span>
            }
            buttonLabel={"Go to My Trade-Ins"}
            cancelLabel={"Close"}
            blocker={blocker}
            buttonHref="/en-US/my-trade-ins"
            buttonParams={queryParams}
          />
        )}
      </AnimatePresence>
      <div className="w-auto lg:self-center xl:w-[660px] p-large md:p-xxx-large flex flex-col gap-x-large md:gap-xx-large">
        <div className="flex flex-col gap-regular">
          <h2 className="text-primary text-6xl">Thanks!</h2>
          <p className="text-tertiary text-2xl">
            Your trade-in has been confirmed.
          </p>
        </div>
        <div className="flex flex-col gap-6 max-w-xl mt-4">
          <h3 className="text-2xl font-bold">Next steps:</h3>
          {/* MAIL-IN VERSION */}
          {isMail && (
            <>
              <div className="flex">
                <div className="bg-red text-white rounded-full w-6 h-6 flex items-center justify-center shrink-0 mr-4">
                  1
                </div>
                <div>
                  First, gather and fully disassemble your LEGO® bricks. Then,
                  follow the step-by-step instructions in the Preparation
                  Guidelines.
                  <div className="mt-4 mb-4">
                    <Link
                      to={`/en-US/preparation-guidelines?state=${queryParams.state}&preselected=${queryParams.takebackMethod === "mail" ? "mail" : "in-store"}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue cursor-pointer font-bold"
                    >
                      <span className="material-symbols-outlined scale-90 translate-y-[0.4rem] mr-1">
                        rule
                      </span>{" "}
                      View Acceptance Criteria
                    </Link>
                  </div>
                  Need to update your trade-in weight before sending it off?
                  Simply visit your{" "}
                  <Link
                    to="/en-US/my-trade-ins"
                    className="text-blue cursor-pointer font-bold"
                  >
                    My Trade-Ins dashboard
                  </Link>{" "}
                  to edit the weight.
                </div>
              </div>
              <div className="flex">
                <div className="bg-red text-white rounded-full w-6 h-6 flex items-center justify-center shrink-0 mr-4">
                  2
                </div>
                <div>
                  Pack up your bricks in a sturdy cardboard shipping box. Seal
                  it with strong packing tape, and be sure it weighs no more
                  than{" "}
                  {weightRestrictions
                    .find((weight) => weight.unit === "lbs")
                    .maxMail.toFixed(2)}{" "}
                  lbs.
                  <div className="mt-4">
                    <Link
                      to={`/en-US/preparation-guidelines?state=${queryParams.state}&preselected=${queryParams.takebackMethod === "mail" ? "mail" : "in-store"}#packagingGuidelines`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue cursor-pointer font-bold"
                    >
                      <span className="material-symbols-outlined scale-90 translate-y-[0.4rem] mr-1">
                        orders
                      </span>
                      View Packaging Guidelines
                    </Link>
                  </div>
                </div>
              </div>
              <div className="flex">
                <div className="bg-red text-white rounded-full w-6 h-6 flex items-center justify-center shrink-0 mr-4">
                  3
                </div>
                <div>
                  <div>
                    Choose how to ship your LEGO Brick Take Back package:
                  </div>
                  <div className="mt-3">
                    <b>Option 1:</b> Download, print, and attach the shipping
                    label to your package.
                  </div>
                  <div className="mt-3">
                    <b>OR</b>
                  </div>
                  <div className="mt-3">
                    <b>Option 2:</b> Generate a shipping QR code to scan at{" "}
                    <Link
                      to="https://www.theupsstore.com/tools/find-a-store"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue cursor-pointer font-bold"
                    >
                      <span>
                        The{"\u00A0"}UPS{"\u00A0"}Store®
                      </span>
                    </Link>
                    —no printer needed!
                  </div>
                  <div className="mt-3 ">
                    <span className="font-bold">Note:</span> Each label is
                    unique to one trade-in package only. If you have multiple
                    packages to send, please create a new trade-in.
                  </div>
                </div>
              </div>
              <div className="flex">
                <div className="bg-red text-white rounded-full w-6 h-6 flex items-center justify-center shrink-0 mr-4">
                  4
                </div>
                <div>
                  Take your package to your nearest{" "}
                  <Link
                    to="https://www.ups.com/dropoff/?loc=en_US"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue cursor-pointer font-bold"
                  >
                    UPS location
                  </Link>
                  .
                </div>
              </div>
              <div className="flex">
                <div className="bg-red text-white rounded-full w-6 h-6 flex items-center justify-center shrink-0 mr-4">
                  5
                </div>
                <div>
                  Wait for your trade-in to be verified before receiving your
                  payment. You can track the status of your trade-in at any time
                  by{" "}
                  <Link
                    to="/en-US/my-trade-ins"
                    className="text-blue cursor-pointer font-bold"
                  >
                    visiting the My Trade-Ins dashboard
                  </Link>
                  .
                </div>
              </div>
            </>
          )}
          {/* IN-STORE DROPOFF VERSION */}
          {!isMail && !isKiosk && (
            <>
              <div className="flex">
                <div className="bg-red text-white rounded-full w-6 h-6 flex items-center justify-center shrink-0 mr-4">
                  1
                </div>
                <div>
                  First, gather and fully disassemble your LEGO® bricks. Then,
                  follow the step-by-step instructions in the Preparation
                  Guidelines.
                  <div className="mt-4 mb-4">
                    <Link
                      to={`/en-US/preparation-guidelines?state=${queryParams.state}&preselected=${queryParams.takebackMethod === "mail" ? "mail" : "in-store"}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue cursor-pointer font-bold"
                    >
                      <span className="material-symbols-outlined scale-90 translate-y-[0.4rem] mr-1">
                        rule
                      </span>{" "}
                      View Acceptance Criteria
                    </Link>
                  </div>
                  Need to update your trade-in weight before your visit? Just
                  visit your{" "}
                  <Link
                    to={t("urls.my_trade_ins")}
                    className="text-blue cursor-pointer font-bold"
                  >
                    My Trade-Ins Dashboard
                  </Link>{" "}
                  to make changes.
                </div>
              </div>
              <div className="flex">
                <div className="bg-red text-white rounded-full w-6 h-6 flex items-center justify-center shrink-0 mr-4">
                  2
                </div>
                <div>
                  Pack up your bricks in a sturdy cardboard box. Keep it
                  unsealed, and be sure it weighs no more than{" "}
                  {weightRestrictions
                    .find((weight) => weight.unit === "lbs")
                    .maxDropOff.toFixed(2)}{" "}
                  lbs.
                  <div className="mt-4">
                    <Link
                      to={`/en-US/preparation-guidelines?state=${queryParams.state}&preselected=${queryParams.takebackMethod === "mail" ? "mail" : "in-store"}#packagingGuidelines`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue cursor-pointer font-bold"
                    >
                      <span className="material-symbols-outlined scale-90 translate-y-[0.4rem] mr-1">
                        orders
                      </span>
                      View Packaging Guidelines
                    </Link>
                  </div>
                </div>
              </div>
              <div className="flex">
                <div className="bg-red text-white rounded-full w-6 h-6 flex items-center justify-center shrink-0 mr-4">
                  3
                </div>
                <div>
                  Visit us in-store at the location below. Have your store
                  check-in QR code ready for the Brick Specialist.
                  <div className="mt-2">
                    <b>Location:</b> {addressString}
                  </div>
                </div>
              </div>
              <div className="flex">
                <div className="bg-red text-white rounded-full w-6 h-6 flex items-center justify-center shrink-0 mr-4">
                  4
                </div>
                <div>
                  Get your LEGO bricks weighed and verified by an in-store Brick
                  Specialist to secure your payment offer.
                </div>
              </div>
            </>
          )}
          {/* IN-STORE KIOSK VERSION */}
          {!isMail && isKiosk && (
            <>
              <div className="flex">
                <div className="bg-red text-white rounded-full w-6 h-6 flex items-center justify-center shrink-0 mr-4">
                  1
                </div>
                <div>
                  First, gather and fully disassemble your LEGO® bricks. Then,
                  follow the step-by-step instructions in the Preparation
                  Guidelines.
                  <div className="mt-4 mb-4">
                    <Link
                      to={`/en-US/preparation-guidelines?state=${queryParams.state}&preselected=${queryParams.takebackMethod === "mail" ? "mail" : "in-store"}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue cursor-pointer font-bold"
                    >
                      <span className="material-symbols-outlined scale-90 translate-y-[0.4rem] mr-1">
                        rule
                      </span>{" "}
                      View Acceptance Criteria
                    </Link>
                  </div>
                  Need to update your trade-in weight before your visit? Just
                  visit your{" "}
                  <Link
                    to={t("urls.my_trade_ins")}
                    className="text-blue cursor-pointer font-bold"
                  >
                    My Trade-Ins Dashboard
                  </Link>{" "}
                  to make changes.
                </div>
              </div>
              <div className="flex">
                <div className="bg-red text-white rounded-full w-6 h-6 flex items-center justify-center shrink-0 mr-4">
                  2
                </div>
                <div>
                  Visit us in-store at your appointment date and time. Have your
                  store check-in QR code ready for the Brick Specialist.
                  <div className="mt-2">
                    <b>Date & time:</b>{" "}
                    {(() => {
                      const startISO = appointment.time || appointment.date;
                      let duration = appointment.duration;
                      if (!duration) {
                        // fallback: recalculate from weight if needed
                        const weight = parseFloat(queryParams.w);
                        duration =
                          typeof weight === "number" && !isNaN(weight)
                            ? calculateDurationByWeight(weight)
                            : 30;
                      }
                      const formatted = formatAppointmentTimeRange(
                        startISO,
                        duration,
                        queryParams[TAKEBACK_METHOD_NAME]
                      );
                      return formatted || "";
                    })()}
                  </div>
                  <div className="mt-2">
                    <b>Location:</b> {addressString}
                  </div>
                  <div className="mt-4">
                    <Link
                      to={`/en-US/preparation-guidelines?state=${queryParams.state}&preselected=${queryParams.takebackMethod === "mail" ? "mail" : "in-store"}#packagingGuidelines`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue cursor-pointer font-bold"
                    >
                      <span className="material-symbols-outlined scale-90 translate-y-[0.4rem] mr-1">
                        orders
                      </span>
                      View Preparation Guidelines
                    </Link>
                  </div>
                </div>
              </div>
              <div className="flex">
                <div className="bg-red text-white rounded-full w-6 h-6 flex items-center justify-center shrink-0 mr-4">
                  3
                </div>
                <div>
                  Get your LEGO bricks weighed and verified by an in-store Brick
                  Specialist to earn your LEGO e-Gift Card.
                </div>
              </div>
            </>
          )}
        </div>
        <Button
          href={t("urls.my_trade_ins")}
          search={`?locale=${queryParams.locale}&state=${queryParams.state}`}
          className="w-fit"
        >
          Go to My Trade-Ins
        </Button>
      </div>
    </div>
  );
}
